import { z } from 'zod';
import { uniqueBy } from '../../shared/collections';
import { ROOM_CODE_RE, isRoomCode } from '../../shared/config';
import type { RoundResults } from '../../shared/results';
import { parseJsonOr } from '../../shared/json';
import { configSchema } from '../../shared/schema-primitives';

const MAX_LOCAL_HISTORY = 50;
/** A browser can visit many rooms; retain only the most recent reconnect tokens. */
const MAX_ROOM_TOKENS = 12;
const MAX_TOKEN_LENGTH = 128;

const localHistoryEntrySchema = z.object({
  roundId: z.string(),
  roomCode: z.string(),
  round: z.number().int(),
  config: configSchema,
  score: z.number().finite(),
  total: z.number().finite(),
  at: z.number().finite(),
});
export type LocalHistoryEntry = z.infer<typeof localHistoryEntrySchema>;

export interface StorageLike {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const PREFIX = 'spellie.';
const TOKEN_MAP_KEY = 'roomTokens';
const HISTORY_KEY = 'history';

export class BrowserStore {
  constructor(private readonly storage: StorageLike | null = null) {}

  get(key: string): string | null {
    try {
      return this.storage?.getItem(PREFIX + key) ?? null;
    } catch {
      return null;
    }
  }

  set(key: string, value: string | null): void {
    try {
      if (value === null) this.storage?.removeItem(PREFIX + key);
      else this.storage?.setItem(PREFIX + key, value);
    } catch {
      // Private browsing and quota errors intentionally make storage best-effort.
    }
  }

  clearUserData(): boolean {
    if (!this.storage) return false;
    try {
      // Snapshot first: browsers may reorder storage keys after a removal.
      const keys: string[] = [];
      for (let index = 0; index < this.storage.length; index++) {
        const key = this.storage.key(index);
        if (key?.startsWith(PREFIX)) keys.push(key);
      }
      for (const key of keys) this.storage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }

  get roomCode(): string | null {
    const code = this.get('roomCode');
    return isRoomCode(code) ? code : null;
  }

  set roomCode(value: string | null) {
    this.set('roomCode', value);
  }

  private tokens(): Record<string, string> {
    const value = parseJsonOr<unknown>(this.get(TOKEN_MAP_KEY), {});
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value).filter(
        ([code, token]) =>
          ROOM_CODE_RE.test(code) && typeof token === 'string' && token.length <= MAX_TOKEN_LENGTH,
      ),
    );
  }

  roomToken(code: string): string | null {
    const tokens = this.tokens();
    const token = tokens[code];
    return typeof token === 'string' && token.length <= MAX_TOKEN_LENGTH ? token : null;
  }

  saveRoomToken(code: string, token: string | null): void {
    const tokens = this.tokens();
    delete tokens[code];
    if (token !== null) tokens[code] = token;
    const entries = Object.entries(tokens).slice(-MAX_ROOM_TOKENS);
    this.set(TOKEN_MAP_KEY, JSON.stringify(Object.fromEntries(entries)));
  }

  history(): LocalHistoryEntry[] {
    const rows = parseJsonOr<unknown>(this.get(HISTORY_KEY), []);
    if (!Array.isArray(rows)) return [];
    return rows
      .flatMap((row) => {
        const parsed = localHistoryEntrySchema.safeParse(row);
        return parsed.success ? [parsed.data] : [];
      })
      .slice(0, MAX_LOCAL_HISTORY);
  }

  addHistory(entry: LocalHistoryEntry): void {
    this.addHistoryBatch([entry]);
  }

  /** Entries are newest first; the first occurrence wins, as with reverse-order single adds. */
  addHistoryBatch(entries: readonly LocalHistoryEntry[]): void {
    if (!entries.length) return;
    const rows = uniqueBy([...entries, ...this.history()], (row) => row.roundId);
    this.set(HISTORY_KEY, JSON.stringify(rows.slice(0, MAX_LOCAL_HISTORY)));
  }

  addResults(roomCode: string, results: RoundResults, playerId: string): void {
    const player = results.players.find((row) => row.id === playerId);
    if (!player) return;
    this.addHistory({
      roundId: results.roundId,
      roomCode,
      round: results.round,
      config: results.config,
      score: player.score,
      total: player.total,
      at: results.endedAt,
    });
  }
}

export function createBrowserStore(storage?: StorageLike): BrowserStore {
  if (storage) return new BrowserStore(storage);
  try {
    return new BrowserStore(localStorage);
  } catch {
    return new BrowserStore(null);
  }
}
